/**
 * /learn — lesson index.
 *
 * A curriculum poster on wide viewports: a phase × track grid (rows are
 * curriculum phases Foundation → Advanced, columns are tracks) so a learner
 * reads the path down each lane. Below `lg` it falls back to PR-1's stacked
 * per-track columns. Guitar (opt-in, all one phase) renders as a separate
 * board rather than a phase-grid lane. Each card surfaces title, est. read
 * minutes, current mastery tier from `nodeMastery()`, a phase chip, a
 * track-colored rail, and a per-lesson completion state (`lessonsCompleted`).
 *
 * Imports the runtime contract from `@music-app/lessons/runtime` (not
 * the components barrel) so the index page's static graph stays free of
 * the heavier MDX-content + audio-player surface. Per-lesson MDX
 * modules are loaded lazily by `<LearnLesson>` when the user actually
 * navigates to one.
 */

import {
  type CSSProperties,
  type ReactNode,
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PageHeader } from "../components/PageHeader.js";
import { useScrollOverflow } from "../lib/use-scroll-overflow.js";
import {
  buildPosterMatrix,
  type PosterColumn,
  type PosterMatrix,
} from "./learn/poster-matrix.js";
import { LineageOverlay } from "./learn/LineageOverlay.js";
import { lessonPracticeRoute } from "./learn/practice-route.js";
import { buildAdjacency, buildEdges, ancestorsOf } from "./learn/lineage.js";
import {
  buildAllItems,
  MVP_NODES,
  MASTERY_PRESETS,
  nodeMastery,
  type CurriculumNode,
  type Item,
  type MasteryThresholds,
  type Phase,
} from "@music-app/learning-system";
import { tokens } from "@music-app/ui-components";
import { useCardsStore, usePrefsStore } from "@music-app/persistence";
import {
  listLessons,
  type LessonMetadata,
} from "@music-app/lessons/runtime";

// Guitar is an opt-in instrument track surfaced separately, never in this
// ear-training learn index. See guitar-harmony-v1.md §5.1.
type LearnTrack = Exclude<CurriculumNode["track"], "counterpoint" | "guitar">;

const TRACK_ORDER: LearnTrack[] = [
  "pitch",
  "modes",
  "intervals",
  "harmony",
  "dictation",
];

const TRACK_LABELS: Record<LearnTrack, string> = {
  pitch: "Pitch",
  modes: "Modes",
  intervals: "Intervals",
  harmony: "Harmony",
  dictation: "Dictation",
};

// Vertical ordering within a track lane: earlier phase sits higher, so a
// learner reads the lane top-to-bottom as the path from foundation to
// advanced. See CURRICULUM.md §2 for the phase table.
const PHASE_RANK: Record<Phase, number> = { "1": 0, "2A": 1, "2B+": 2, "3": 3 };

const PHASE_LABELS: Record<Phase, string> = {
  "1": "Foundation",
  "2A": "Extensions",
  "2B+": "Rhythm",
  "3": "Advanced",
};

// Per-track identity color, resolved from the shared design tokens (never an
// ad-hoc hex). Applied as the tile's left rail via inline style so it can't be
// clobbered by Tailwind border-color ordering. `guitar` falls back to a neutral
// ink so the opt-in lane stays visually quiet.
function trackColor(track: string): string {
  const map = tokens.colors.trackColor as Record<string, string>;
  return map[track] ?? tokens.colors.inkFaded;
}

// The poster grid (rows = phase, columns = track) needs the horizontal room to
// keep every lane side by side; below this width we fall back to the stacked
// per-track columns. Page-local UI state, so a small matchMedia hook rather
// than a store.
function useIsWide(query = "(min-width: 1024px)"): boolean {
  const [wide, setWide] = useState(() =>
    typeof window === "undefined" ? true : window.matchMedia(query).matches,
  );
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (): void => setWide(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);
  return wide;
}

// Warm banding on alternating phase rows — reads as a curriculum poster's
// horizontal rules without competing with the track colors.
const PHASE_BAND_COLOR = tokens.colors.paperWarm;


type LessonCardState = "not-started" | "completed";

interface LessonCard {
  metadata: LessonMetadata;
  node: CurriculumNode;
  tier: ReturnType<typeof nodeMastery>["tier"];
  state: LessonCardState;
  phase: Phase;
}

/**
 * Pure helper — group authored lessons by their curriculum track so the
 * page can render a column per track. Lessons whose `nodeId` has no
 * matching curriculum node are filtered out (the runtime test would
 * already catch this; defending in case authoring slips through).
 */
export function groupLessonsByTrack(
  lessons: LessonMetadata[],
  nodes: CurriculumNode[],
  items: Item[],
  cards: Parameters<typeof nodeMastery>[2],
  lessonsCompleted: ReadonlySet<string>,
  thresholds: MasteryThresholds = MASTERY_PRESETS.standard,
): { track: CurriculumNode["track"]; label: string; lessons: LessonCard[] }[] {
  const byTrack = new Map<CurriculumNode["track"], LessonCard[]>();
  for (const meta of lessons) {
    const node = nodes.find((n) => n.id === meta.nodeId);
    if (!node) continue;
    const m = nodeMastery(node, items, cards, thresholds);
    const card: LessonCard = {
      metadata: meta,
      node,
      tier: m.tier,
      state: lessonsCompleted.has(meta.nodeId) ? "completed" : "not-started",
      phase: node.phase,
    };
    const existing = byTrack.get(node.track) ?? [];
    existing.push(card);
    byTrack.set(node.track, existing);
  }
  return TRACK_ORDER.flatMap((track) => {
    const lessonsInTrack = byTrack.get(track);
    if (!lessonsInTrack || lessonsInTrack.length === 0) return [];
    // Stable sort by phase so the lane reads foundation → advanced. Array.sort
    // is stable, so same-phase lessons keep their curriculum (registry) order.
    const ordered = [...lessonsInTrack].sort(
      (a, b) => PHASE_RANK[a.phase] - PHASE_RANK[b.phase],
    );
    return [{ track, label: TRACK_LABELS[track], lessons: ordered }];
  });
}

export function LearnPage(): ReactNode {
  const cards = useCardsStore((s) => s.cards);
  const lessonsCompletedArr = usePrefsStore((s) => s.lessonsCompleted);
  const masteryPreset = usePrefsStore((s) => s.masteryPreset);
  const enabledInstrumentTracks = usePrefsStore((s) => s.enabledInstrumentTracks);
  const guitarEnabled = enabledInstrumentTracks.includes("guitar");

  const items = useMemo(() => buildAllItems(), []);
  const lessons = useMemo(() => listLessons(), []);
  const completed = useMemo(
    () => new Set(lessonsCompletedArr),
    [lessonsCompletedArr],
  );
  const thresholds = MASTERY_PRESETS[masteryPreset];
  const tracks = useMemo(
    () => groupLessonsByTrack(lessons, MVP_NODES, items, cards, completed, thresholds),
    [lessons, items, cards, completed, thresholds],
  );

  // Guitar lessons — shown only when the guitar track is enabled.
  const guitarLessons = useMemo<LessonCard[]>(() => {
    if (!guitarEnabled) return [];
    return lessons.flatMap((meta) => {
      const node = MVP_NODES.find((n) => n.id === meta.nodeId && n.track === "guitar");
      if (!node) return [];
      const m = nodeMastery(node, items, cards, thresholds);
      return [{
        metadata: meta,
        node,
        tier: m.tier,
        state: (completed.has(meta.nodeId) ? "completed" : "not-started") as LessonCardState,
        phase: node.phase,
      }];
    });
  }, [guitarEnabled, lessons, items, cards, thresholds, completed]);

  // Ear-training columns drive the poster grid. Guitar is opt-in and all one
  // phase (16 nodes deep on the ADVANCED rung), so it never joins the phase
  // grid — it would balloon that row into a mostly-empty band. It renders as a
  // separate labelled board instead, matching how guitar is surfaced elsewhere.
  const earColumns = useMemo<PosterColumn<LessonCard>[]>(
    () =>
      tracks.map((t) => ({ track: t.track, label: t.label, lessons: t.lessons })),
    [tracks],
  );
  const guitarColumn = useMemo<PosterColumn<LessonCard> | null>(
    () =>
      guitarLessons.length > 0
        ? { track: "guitar", label: "Guitar", lessons: guitarLessons }
        : null,
    [guitarLessons],
  );

  const matrix = useMemo(() => buildPosterMatrix(earColumns), [earColumns]);
  const isWide = useIsWide();
  const hasLessons = earColumns.length > 0 || guitarColumn !== null;

  const [searchParams] = useSearchParams();
  const trackParam = searchParams.get("track");
  useEffect(() => {
    if (!trackParam) return;
    const el = document.getElementById(`track-${trackParam}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start", inline: "start" });
    }
  }, [trackParam, isWide]);

  return (
    <div className="max-w-5xl mx-auto py-9 px-8">
      <PageHeader
        eyebrow="lessons"
        title="Read & listen"
        emphasis="listen"
        subhead="Short lessons pairing prose with audio examples and a check-for-understanding exercise. Read one before you drill the matching skill."
      />

      {!hasLessons && (
        <p className="text-inkSoft italic">No lessons authored yet.</p>
      )}

      {hasLessons &&
        (isWide ? (
          <>
            {earColumns.length > 0 && <PosterGrid matrix={matrix} />}
            {guitarColumn && <GuitarBoard column={guitarColumn} />}
          </>
        ) : (
          <StackedColumns
            columns={guitarColumn ? [...earColumns, guitarColumn] : earColumns}
          />
        ))}
    </div>
  );
}

// ─── Layouts ──────────────────────────────────────────────────────────────

/** Wide viewport: a curriculum poster — rows are phases, columns are tracks. */
function PosterGrid({ matrix }: { matrix: PosterMatrix<LessonCard> }): ReactNode {
  const laneCount = matrix.lanes.length;
  const gridRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { canScrollLeft, canScrollRight } = useScrollOverflow(scrollRef);
  const [active, setActive] = useState<string | null>(null);

  const posterNodes = useMemo(
    () =>
      matrix.lanes.flatMap((lane) =>
        [...lane.cells.values()].flatMap((cards) => cards.map((c) => c.node)),
      ),
    [matrix],
  );
  const edges = useMemo(() => buildEdges(posterNodes), [posterNodes]);
  const adjacency = useMemo(() => buildAdjacency(posterNodes), [posterNodes]);
  const litSet = useMemo(() => {
    if (!active) return null;
    const s = ancestorsOf(active, adjacency);
    s.add(active);
    return s;
  }, [active, adjacency]);

  const emphasisFor = (nodeId: string): TileEmphasis => {
    if (!litSet) return "none";
    if (nodeId === active) return "origin";
    return litSet.has(nodeId) ? "lit" : "dim";
  };

  // The 5-lane grid needs ~73rem, more than the app shell's max-w-5xl allows,
  // so the poster breaks out: centered on the viewport, capped at 78rem. The
  // 3rem viewport inset over-compensates for the vertical scrollbar's width
  // (100vw includes it), so the breakout can never cause body horizontal
  // scroll. When the poster still clips, edge fades signal the hidden lanes.
  return (
    <div className="relative left-1/2 -translate-x-1/2 w-[min(78rem,calc(100vw_-_3rem))]">
      <div
        ref={scrollRef}
        className="poster-scrollbar overflow-x-auto pb-2"
        role="group"
        aria-label="Lessons by track and phase"
      >
        <div
          ref={gridRef}
          className="grid gap-x-4 relative"
          style={{
            gridTemplateColumns: `2.75rem repeat(${laneCount}, minmax(13rem, 1fr))`,
          }}
        >
          <LineageOverlay
            gridRef={gridRef}
            edges={edges}
            litNodeIds={litSet}
            colorForTrack={trackColor}
          />

          {/* Alternating phase-row bands, behind the tiles. */}
          {matrix.phases.map((phase, r) =>
            r % 2 === 1 ? (
              <div
                key={`band-${phase}`}
                aria-hidden="true"
                className="rounded"
                style={{
                  gridColumn: "1 / -1",
                  gridRow: r + 2,
                  backgroundColor: PHASE_BAND_COLOR,
                }}
              />
            ) : null,
          )}

          {/* Track headers across the top. */}
          {matrix.lanes.map((lane, c) => (
            <div
              key={`head-${lane.track}`}
              className="flex items-center gap-2 pb-3 z-10"
              style={{ gridColumn: c + 2, gridRow: 1 }}
            >
              <h2
                id={`track-${lane.track}`}
                className="font-mono text-[10px] uppercase tracking-[0.18em]"
                style={{ color: trackColor(lane.track) }}
              >
                {lane.label}
              </h2>
              <span
                className="h-px flex-1 rounded"
                style={{ backgroundColor: trackColor(lane.track), opacity: 0.4 }}
                aria-hidden="true"
              />
            </div>
          ))}

          {/* Phase ruler + one cell per (phase, lane). */}
          {matrix.phases.map((phase, r) => (
            <Fragment key={`row-${phase}`}>
              <div
                className="flex items-center justify-center z-10"
                style={{ gridColumn: 1, gridRow: r + 2 }}
              >
                <span
                  className="font-mono text-[9px] uppercase tracking-[0.14em] text-inkFaded whitespace-nowrap"
                  style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                >
                  {PHASE_LABELS[phase]}
                </span>
              </div>
              {matrix.lanes.map((lane, c) => (
                <div
                  key={`${lane.track}-${phase}`}
                  className="flex flex-col gap-3 py-2 z-10"
                  style={{ gridColumn: c + 2, gridRow: r + 2 }}
                >
                  {(lane.cells.get(phase) ?? []).map((card) => (
                    <LessonCardTile
                      key={card.metadata.nodeId}
                      card={card}
                      emphasis={emphasisFor(card.metadata.nodeId)}
                      onActivate={setActive}
                    />
                  ))}
                </div>
              ))}
            </Fragment>
          ))}
        </div>
      </div>
      {canScrollLeft && (
        <div
          data-testid="poster-fade-left"
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 left-0 z-20 w-12 bg-gradient-to-r from-paper"
        />
      )}
      {canScrollRight && (
        <div
          data-testid="poster-fade-right"
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-0 z-20 w-12 bg-gradient-to-l from-paper"
        />
      )}
    </div>
  );
}

/** Narrow viewport: PR-1's per-track stacked columns. */
function StackedColumns({
  columns,
}: {
  columns: PosterColumn<LessonCard>[];
}): ReactNode {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-10">
      {columns.map((column) => (
        <section key={column.track} aria-labelledby={`track-${column.track}`}>
          <div className="flex items-center gap-2 mb-3">
            <h2
              id={`track-${column.track}`}
              className="font-mono text-[10px] uppercase tracking-[0.18em]"
              style={{ color: trackColor(column.track) }}
            >
              {column.label}
            </h2>
            <span
              className="h-px flex-1 rounded"
              style={{ backgroundColor: trackColor(column.track), opacity: 0.4 }}
              aria-hidden="true"
            />
          </div>
          <div className="space-y-3">
            {column.lessons.map((card) => (
              <LessonCardTile key={card.metadata.nodeId} card={card} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

/**
 * Guitar (opt-in) below the poster — a single labelled board rather than a
 * phase-grid lane, since all its nodes share one phase. Its own multi-column
 * grid keeps 16 tiles compact instead of a tall single stack.
 */
function GuitarBoard({
  column,
}: {
  column: PosterColumn<LessonCard>;
}): ReactNode {
  return (
    <section aria-labelledby="track-guitar" className="mt-10">
      <div className="flex items-center gap-2 mb-3">
        <h2
          id="track-guitar"
          className="font-mono text-[10px] uppercase tracking-[0.18em]"
          style={{ color: trackColor("guitar") }}
        >
          {column.label}
        </h2>
        <span
          className="h-px flex-1 rounded"
          style={{ backgroundColor: trackColor("guitar"), opacity: 0.4 }}
          aria-hidden="true"
        />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {column.lessons.map((card) => (
          <LessonCardTile key={card.metadata.nodeId} card={card} />
        ))}
      </div>
    </section>
  );
}

// Poster lineage state for a tile. Non-poster layouts pass "none". Nothing is
// hidden on dim — lineage is a suggestion, not a gate (VISION §3.5).
type TileEmphasis = "none" | "lit" | "dim" | "origin";

interface LessonCardTileProps {
  card: LessonCard;
  emphasis?: TileEmphasis;
  /** Poster only — report hover/focus so the grid can trace this tile's lineage. */
  onActivate?: (nodeId: string | null) => void;
}

function LessonCardTile({
  card,
  emphasis = "none",
  onActivate,
}: LessonCardTileProps): ReactNode {
  const { metadata, node, tier, state, phase } = card;
  const isCompleted = state === "completed";
  const drillRoute = lessonPracticeRoute(metadata.nodeId);
  const railColor = trackColor(node.track);

  const style: CSSProperties = {
    borderLeftColor: railColor,
    borderLeftWidth: 3,
  };
  if (emphasis === "dim") style.opacity = 0.5;
  if (emphasis === "origin") style.boxShadow = `0 0 0 2px ${railColor}`;

  const activate = onActivate ? () => onActivate(metadata.nodeId) : undefined;
  const deactivate = onActivate ? () => onActivate(null) : undefined;

  return (
    <article
      data-node-id={metadata.nodeId}
      onMouseEnter={activate}
      onMouseLeave={deactivate}
      onFocus={activate}
      onBlur={deactivate}
      className={`border transition motion-reduce:transition-none ${
        isCompleted
          ? "bg-paperWarm border-success/40 hover:border-success"
          : "bg-paper border-ink/10 hover:border-accent"
      }`}
      style={style}
    >
      <Link
        to={`/learn/${metadata.nodeId}`}
        className="block p-4 group"
        aria-label={`Lesson: ${metadata.title}${isCompleted ? " (completed)" : ""}`}
      >
        <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-1 mb-1">
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkFaded">
            {tier}
          </span>
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 justify-end">
            <span
              className="font-mono text-[10px] uppercase tracking-[0.18em]"
              style={{ color: trackColor(node.track), opacity: 0.85 }}
            >
              {PHASE_LABELS[phase]}
            </span>
            {isCompleted && (
              <span
                className="font-mono text-[10px] uppercase tracking-[0.18em] text-success"
                aria-label="completed"
              >
                ✓ done
              </span>
            )}
          </div>
        </div>
        <h3 className="font-serif italic text-lg font-medium text-ink mb-1.5 leading-snug break-words">
          {metadata.title}
        </h3>
        <div className="flex items-baseline justify-between gap-2 mt-3">
          <span className="text-xs text-inkSoft">
            ~{metadata.estReadMinutes} min read
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-inkSoft group-hover:text-accent transition-colors">
            {isCompleted ? "review →" : "start lesson →"}
          </span>
        </div>
      </Link>
      {drillRoute !== undefined && (
        <div className="px-4 pb-3 pt-2 border-t border-ink/5">
          <Link
            to={drillRoute}
            className="font-mono text-[9px] uppercase tracking-[0.18em] text-inkFaded hover:text-accent transition-colors"
          >
            Practice this →
          </Link>
        </div>
      )}
    </article>
  );
}
